import { Check, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../../components/ui/overlays';
import { Button, Card, CardBody, CardHeader, Field, Input } from '../../components/ui/primitives';
import { useChangePassword, useSession, useUpdateProfile } from '../../hooks/queries';

const PASSWORD_MIN = 12;

/** Your own name and password. Available to every signed-in user, whatever their role. */
export const AccountCard = ({ className }: { className?: string }) => {
  const { data: session } = useSession();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const toast = useToast();

  const [name, setName] = useState('');
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [saved, setSaved] = useState(false);

  useEffect(() => setName(session?.user?.name ?? ''), [session?.user?.name]);

  const nameChanged = Boolean(session?.user && name.trim() && name !== session.user.name);

  const submitPassword = async () => {
    if (passwords.next.length < PASSWORD_MIN) {
      toast.error(`Use at least ${PASSWORD_MIN} characters`);
      return;
    }
    if (passwords.next !== passwords.confirm) {
      toast.error('The two new passwords do not match');
      return;
    }
    try {
      await changePassword.mutateAsync({
        currentPassword: passwords.current,
        newPassword: passwords.next,
      });
      setPasswords({ current: '', next: '', confirm: '' });
      toast.success('Password changed — other sessions have been signed out');
    } catch (error) {
      toast.error(error);
    }
  };

  return (
    <Card className={className}>
      <CardHeader
        title="Your account"
        description={session?.user?.email ?? 'Signed in'}
        actions={
          nameChanged ? (
            <Button
              size="sm"
              variant="primary"
              loading={updateProfile.isPending}
              icon={saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
              onClick={async () => {
                try {
                  await updateProfile.mutateAsync({ name: name.trim() });
                  setSaved(true);
                  window.setTimeout(() => setSaved(false), 2000);
                  toast.success('Name updated');
                } catch (error) {
                  toast.error(error);
                }
              }}
            >
              Save name
            </Button>
          ) : null
        }
      />
      <CardBody className="space-y-4">
        <Field label="Display name" htmlFor="account-name">
          <Input
            id="account-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <div className="border-t border-line pt-4">
          <span className="field-label">Change password</span>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Current" htmlFor="pw-current">
              <Input
                id="pw-current"
                type="password"
                autoComplete="current-password"
                value={passwords.current}
                onChange={(event) => setPasswords({ ...passwords, current: event.target.value })}
              />
            </Field>
            <Field label="New" htmlFor="pw-new">
              <Input
                id="pw-new"
                type="password"
                autoComplete="new-password"
                value={passwords.next}
                onChange={(event) => setPasswords({ ...passwords, next: event.target.value })}
              />
            </Field>
            <Field label="Confirm" htmlFor="pw-confirm">
              <Input
                id="pw-confirm"
                type="password"
                autoComplete="new-password"
                value={passwords.confirm}
                onChange={(event) => setPasswords({ ...passwords, confirm: event.target.value })}
              />
            </Field>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="primary"
              size="sm"
              loading={changePassword.isPending}
              disabled={!passwords.current || !passwords.next}
              onClick={submitPassword}
            >
              Change password
            </Button>
            <span className="text-xs text-faint">
              At least {PASSWORD_MIN} characters. Signs out every other session.
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
